import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spinner, Text, Button, makeStyles, tokens } from '@fluentui/react-components';
import { HomeRegular, ArrowLeftRegular } from '@fluentui/react-icons';
import DrillDownView from '../DrillDownView';
import IssueList from '../IssueList';
import Breadcrumb from '../Breadcrumb';
import { fetchIssues } from '../../services/api';
import { Issue, DrillDownItem, BreadcrumbItem } from '../../types';

interface IssueViewProps {
  apiUrl?: string;
  onNavigate?: (path: string) => void;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    height: 'calc(100vh - 2rem)',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalL,
  },
  leftColumn: {
    width: '68%',
    display: 'flex',
    flexDirection: 'column',
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingRight: tokens.spacingVerticalL,
    overflowY: 'auto',
  },
  rightColumn: {
    width: '32%',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginBottom: tokens.spacingVerticalM,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '80vh',
    gap: tokens.spacingVerticalM,
  },
});

const IssueView: React.FC<IssueViewProps> = ({ apiUrl, onNavigate }) => {
  const { domain, category, subcategory, issue } = useParams<{ 
    domain: string; 
    category: string; 
    subcategory: string;
    issue: string;
  }>();
  const [issues, setIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const styles = useStyles();

  useEffect(() => {
    const loadIssues = async (): Promise<void> => {
      try {
        const data = await fetchIssues();
        setIssues(data);
        setLoading(false);
      } catch (error) {
        console.error('Failed to load issues:', error);
        setLoading(false);
      }
    };
    loadIssues();
  }, []);

  const rootCauses = useMemo<DrillDownItem[]>(() => {
    if (!domain || !category || !subcategory || !issue) return [];
    const rootCauseMap = new Map<string, Issue>();
    issues
      .filter(
        (i) =>
          i.Taxonomy.Domain === decodeURIComponent(domain) &&
          i.Taxonomy.Category === decodeURIComponent(category) &&
          i.Taxonomy.Subcategory === decodeURIComponent(subcategory) &&
          i.Taxonomy.Issue === decodeURIComponent(issue)
      )
      .forEach((i) => {
        if (!rootCauseMap.has(i.Taxonomy.RootCause)) {
          rootCauseMap.set(i.Taxonomy.RootCause, i);
        }
      });
    return Array.from(rootCauseMap.values()).map((i) => ({
      label: i.Taxonomy.RootCause,
      value: i.Path,
      issue: i,
    }));
  }, [issues, domain, category, subcategory, issue]);

  const handleRootCauseClick = (rootCause: DrillDownItem): void => {
    if (!domain || !category || !subcategory || !issue) return;
    const path = `/${encodeURIComponent(domain)}/${encodeURIComponent(category)}/${encodeURIComponent(subcategory)}/${encodeURIComponent(issue)}/${encodeURIComponent(rootCause.value)}`;
    if (onNavigate) {
      onNavigate(path);
    } else {
      navigate(path);
    }
  };

  const handleBack = (): void => {
    if (!domain || !category || !subcategory) return;
    const path = `/${encodeURIComponent(domain)}/${encodeURIComponent(category)}/${encodeURIComponent(subcategory)}`;
    if (onNavigate) {
      onNavigate(path);
    } else {
      navigate(path);
    }
  };

  const handleReset = (): void => {
    if (onNavigate) {
      onNavigate('/');
    } else {
      navigate('/');
    }
  };

  const handleIssueCardClick = (issue: Issue): void => {
    const path = `/${encodeURIComponent(issue.Taxonomy.Domain)}/${encodeURIComponent(issue.Taxonomy.Category)}/${encodeURIComponent(issue.Taxonomy.Subcategory)}/${encodeURIComponent(issue.Taxonomy.Issue)}/${encodeURIComponent(issue.Path)}`;
    if (onNavigate) {
      onNavigate(path);
    } else {
      navigate(path);
    }
  };

  const breadcrumbPath: BreadcrumbItem[] =
    domain && category && subcategory && issue
      ? [
          { label: decodeURIComponent(domain), value: domain },
          { label: decodeURIComponent(category), value: category },
          { label: decodeURIComponent(subcategory), value: subcategory },
          { label: decodeURIComponent(issue), value: issue },
        ]
      : [];

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" />
        <Text>Loading issues...</Text>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* LEFT COLUMN - Navigation */}
      <div className={styles.leftColumn}>
        <Breadcrumb path={breadcrumbPath} />
        <div className={styles.headerRow}>
          <Text size={500} weight="semibold">
            {issue ? `Root Causes for ${decodeURIComponent(issue)}` : 'Root Causes'}
          </Text>
          <div style={{ display: 'flex', gap: tokens.spacingVerticalXS }}>
            <Button
              icon={<ArrowLeftRegular />}
              appearance="subtle"
              onClick={handleBack}
              size="small"
            >
              Back
            </Button>
            <Button
              icon={<HomeRegular />}
              appearance="subtle"
              onClick={handleReset}
              size="small"
            >
              Reset
            </Button>
          </div>
        </div>
        <DrillDownView
          items={rootCauses}
          onItemClick={handleRootCauseClick}
        />
      </div>

      {/* RIGHT COLUMN - Issue List */}
      <div className={styles.rightColumn}>
        <IssueList
          issues={issues}
          currentPath={domain && category && subcategory && issue ? { domain, category, subcategory, issue } : undefined}
          onIssueClick={handleIssueCardClick}
        />
      </div>
    </div>
  );
};

export default IssueView;
